import { useState, useCallback, useEffect, useRef } from 'react';
import { bookingApi, salonApi } from '../services/api';
import { startOfWeek, endOfWeek, addWeeks, formatISO } from 'date-fns';

export const useBookings = () => {
  const [bookings,    setBookings]    = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [schedule,    setSchedule]    = useState(null);

  const currentWeekRef   = useRef(currentWeek);
  const fetchInFlight    = useRef(false);
  const settingsLoaded   = useRef(false);

  // Sync ref so fetchBookings always sees latest week without re-creating
  useEffect(() => { currentWeekRef.current = currentWeek; }, [currentWeek]);

  // Load settings ONCE — no duplicate calls
  useEffect(() => {
    if (settingsLoaded.current) return;
    settingsLoaded.current = true;
    let cancelled = false;
    salonApi.getSettings()
      .then(res => { if (!cancelled) setSchedule(res.data.salon.working_schedule); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // fetchBookings no longer depends on currentWeek — reads from ref instead
  const fetchBookings = useCallback(async (weekDate) => {
    const target = weekDate ?? currentWeekRef.current;

    // Prevent concurrent duplicate fetches
    if (fetchInFlight.current) return;
    fetchInFlight.current = true;

    setLoading(true);
    setError(null);
    try {
      const from = formatISO(startOfWeek(target, { weekStartsOn: 1 }));
      const to   = formatISO(endOfWeek(target,   { weekStartsOn: 1 }));
      const res  = await bookingApi.getAll(from, to);
      setBookings(res.data.bookings);
    } catch (err) {
      setError(err.response?.data?.error || 'Σφάλμα φόρτωσης κρατήσεων');
    } finally {
      setLoading(false);
      fetchInFlight.current = false;
    }
  }, []); // ← κενό array: δεν ξαναφτιάχνεται ποτέ

  const createBooking = async (data) => {
    try {
      const startsAt = new Date(data.starts_at);
      const endsAt   = new Date(startsAt.getTime() + data.service_duration * 60000);

      const avail = await bookingApi.checkAvailability(
        formatISO(startsAt),
        formatISO(endsAt),
        null,
        data.staff_id || null
      );

      if (!avail.data.available) return { success: false, conflict: avail.data };

      const res = await bookingApi.create(data);
      setBookings(prev => [...prev, res.data.booking]);
      return { success: true, booking: res.data.booking };
    } catch (err) {
      if (err.response?.status === 409) return { success: false, conflict: err.response.data };
      throw err;
    }
  };

  const updateBooking = async (id, data) => {
    const res = await bookingApi.update(id, data);
    setBookings(prev => prev.map(b => b.id === id ? res.data.booking : b));
    return res.data.booking;
  };

  const cancelBooking = async (id) => {
    await bookingApi.cancel(id);
    setBookings(prev => prev.map(b => b.id === id ? { ...b, status: 'cancelled' } : b));
  };

  const goToWeek = useCallback((offset) => {
    const newWeek = addWeeks(currentWeekRef.current, offset);
    currentWeekRef.current = newWeek;
    setCurrentWeek(newWeek);
    fetchBookings(newWeek);
  }, [fetchBookings]);

  return {
    bookings, loading, error, currentWeek, schedule,
    fetchBookings, createBooking, updateBooking, cancelBooking, goToWeek
  };
};